import { Card, Container, PageContainer } from '@amelio/ui-library';

import { AssignReviewerHeader } from './AssignReviewerHeader/AssignReviewerHeader';
import { ReviewerSelection } from './ReviewerSelection/ReviewerSelection';

import type { User } from '@/types/directory/User';
import type { Reviewee } from '@/utils/reviewEvaluatorSelection';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

interface AddReviewerParams {
    revieweeId: string;
    reviewers: User[];
}

interface RemoveReviewerParams {
    revieweeId: string;
    reviewerId: string;
}

interface AssignReviewerProps {
    reviewees: Reviewee[];
    isLoading?: boolean;
    isSaving?: boolean;
    isEvaluationLaunched?: boolean;
    onAddReviewer: (params: AddReviewerParams) => void;
    onRemoveReviewer: (params: RemoveReviewerParams) => void;
    onRemoveReviewee?: (revieweeId: string) => void;
}

export const AssignReviewer = ({
    reviewees,
    isLoading,
    isSaving,
    isEvaluationLaunched,
    onAddReviewer,
    onRemoveReviewer,
    onRemoveReviewee,
}: AssignReviewerProps) => {
    return (
        <PageContainer className={'flex flex-1 flex-col'}>
            {isLoading ? (
                <Skeleton>
                    <Container direction={'row'}>
                        {/* Header Card Section */}
                        <AssignReviewerHeaderSkeleton />

                        {/* Reviewees Section */}
                        <div className={'relative ml-30'}>
                            <Container
                                mt={'section'}
                                className={'flex-col justify-end'}
                            >
                                <RevieweesListSkeleton />
                            </Container>
                        </div>
                    </Container>
                </Skeleton>
            ) : (
                <AssignReviewerHeader
                    reviewees={reviewees}
                    onRemoveReviewee={onRemoveReviewee}
                    isEvaluationLaunched={isEvaluationLaunched}
                />
            )}

            <ReviewerSelection
                reviewees={reviewees}
                isLoading={isLoading}
                isSaving={isSaving}
                onAddReviewer={onAddReviewer}
                onRemoveReviewer={onRemoveReviewer}
            />
        </PageContainer>
    );
};

// Skeleton Components for better readability
const AssignReviewerHeaderSkeleton = () => (
    <Card
        className={'max-w-150 self-start px-8 py-8'}
        borderRadius={'2xl'}
    >
        <div className={'flex items-center gap-6'}>
            <div
                className={
                    'bg-primary-50 flex aspect-square h-16 w-16 shrink-0 items-center justify-center rounded-full'
                }
            >
                <Bone className={'h-8 w-8'} />
            </div>

            <div>
                <Bone className={'h-8 w-48'} />

                <Bone className={'mt-1 h-4 w-64'} />
            </div>
        </div>
    </Card>
);

const RevieweesListSkeleton = () => (
    <Card
        className={'relative'}
        borderRadius={'2xl'}
    >
        <Bone className={'absolute -top-3.5 -right-6 h-6 w-20 rounded-full'} />

        <Container
            direction={'col'}
            gap={'sm'}
            px={'sm'}
            pb={'sm'}
            mt={'xs'}
        >
            {Array.from({ length: 3 }).map((_, index) => (
                <RevieweeCardSkeleton key={index} />
            ))}
        </Container>
    </Card>
);

const RevieweeCardSkeleton = () => (
    <Card
        className={'flex flex-row items-center justify-between gap-2 bg-white px-2'}
        padding={'none'}
        borderRadius={'2xl'}
    >
        <div className={'flex items-center gap-2'}>
            <Bone className={'h-12 w-12 rounded-full'} />

            <div>
                <Bone className={'h-5 w-32'} />

                <Bone className={'mt-1 h-3 w-24'} />
            </div>
        </div>

        <div className={'bg-base-100 rounded-2xl p-4'}>
            <Bone className={'h-6 w-6'} />
        </div>
    </Card>
);
